import { Controller } from "@hotwired/stimulus";
import "./component.scss";

// Connects to data-controller="shop-express-checkout"
export default class extends Controller {
  static values = {
    createIntentUrl: String,
    amount: Number,
    currency: String,
    publishableKey: String,
  };

  static targets = ["expressCheckout", "divider", "errorMessage"];

  connect() {
    this.stripe = Stripe(this.publishableKeyValue);
    this.#initializeExpressCheckout();
  }

  disconnect() {
    if (this.errorTimeout) {
      clearTimeout(this.errorTimeout);
    }
  }

  async #initializeExpressCheckout() {
    // Create elements in "deferred" mode - no client secret yet
    this.elements = this.stripe.elements({
      mode: "payment",
      amount: this.amountValue,
      currency: this.currencyValue,
      locale: "de",
      appearance: {
        theme: "stripe",
        variables: {
          borderRadius: "12px",
        },
      },
    });

    const expressCheckout = this.elements.create("expressCheckout", {
      buttonType: {
        applePay: "plain",
        googlePay: "plain",
      },
      buttonTheme: {
        applePay: "black",
        googlePay: "black",
      },
      buttonHeight: 45,
      layout: {
        maxColumns: 1,
        overflow: "never",
      },
      paymentMethods: {
        applePay: "auto",
        googlePay: "auto",
        // Disable other methods - we only want Apple Pay and Google Pay
        paypal: "never",
        link: "never",
        klarna: "never",
        amazonPay: "never",
      },
    });

    expressCheckout.mount(this.expressCheckoutTarget);

    // Handle confirmation
    expressCheckout.on("confirm", async () => {
      await this.#handleExpressCheckoutConfirm();
    });

    // Check if any express checkout methods are available
    expressCheckout.on("ready", ({ availablePaymentMethods }) => {
      if (availablePaymentMethods) {
        // Show divider when express checkout is available
        if (this.hasDividerTarget) {
          this.dividerTarget.hidden = false;
        }
        this.element.classList.add(
          "c-shop_express_checkout-container--visible"
        );
      } else {
        // No express checkout methods available - hide everything
        this.element.style.display = "none";
      }
    });
  }

  async #handleExpressCheckoutConfirm() {
    try {
      // 1. Create PaymentIntent on server
      const response = await fetch(this.createIntentUrlValue, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]')
            ?.content,
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || "Failed to create payment intent");
      }

      const { client_secret, return_url } = await response.json();

      // 2. Confirm payment with the client secret
      const { error } = await this.stripe.confirmPayment({
        elements: this.elements,
        clientSecret: client_secret,
        confirmParams: {
          return_url: return_url,
        },
      });

      if (error) {
        // This will only be reached if there's an immediate error
        console.error("Payment failed:", error);
        this.#showError(error.message);
      }
      // Otherwise, customer is redirected to return_url
    } catch (error) {
      console.error("Express checkout error:", error);
      this.#showError(error.message);
    }
  }

  #showError(message) {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message;
      this.errorMessageTarget.hidden = false;

      // Auto-hide after 6 seconds
      if (this.errorTimeout) {
        clearTimeout(this.errorTimeout);
      }
      this.errorTimeout = setTimeout(() => {
        this.#hideError();
      }, 6000);
    }
  }

  #hideError() {
    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.hidden = true;
      this.errorMessageTarget.textContent = "";
    }
  }
}
